import { useState, useCallback, useRef, useEffect } from "react";
import { GetPaymentNoticeReport } from "../GetPaymentNotice";
import { useAuth } from "@/utils/useAuth";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";

interface UsePaymentNoticeStatsProps {
  /** Data inicial do período (opcional) */
  startDate?: string;
  /** Data final do período (opcional) */
  endDate?: string;
  /** ID do local selecionado */
  selectedLocation?: string;
  /** ID do profissional selecionado */
  selectedPractitioner?: string;
  /** Status do pagamento (opcional) */
  paymentStatus?: string;
  /** Tamanho da página (opcional) */
  pageSize?: number;
}

interface UsePaymentNoticeStatsResult {
  /** Dados dos pagamentos */
  data: {
    paymentNotices: fhir5.PaymentNotice[];
    patients: fhir5.Patient[];
    slots: fhir5.Slot[];
    appointments: fhir5.Appointment[];
  };
  /** Estado de carregamento */
  loading: boolean;
  /** Estado de erro */
  error: Error | null;
  /** Função para buscar os dados */
  fetchStats: () => Promise<void>;
}

/**
 * Extrai apenas os recursos do tipo PaymentNotice do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de PaymentNotice
 */
function extractPaymentNotices(bundle?: fhir5.Bundle): fhir5.PaymentNotice[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.PaymentNotice> =>
        entry.resource?.resourceType === "PaymentNotice"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.PaymentNotice => resource !== undefined
    );
}

/**
 * Extrai apenas os recursos do tipo Patient do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Patient
 */
function extractPatients(bundle?: fhir5.Bundle): fhir5.Patient[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Patient> =>
        entry.resource?.resourceType === "Patient"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Patient => resource !== undefined);
}

/**
 * Extrai apenas os recursos do tipo Slot do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Slot
 */
function extractAppointments(bundle?: fhir5.Bundle): fhir5.Appointment[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Appointment> =>
        entry.resource?.resourceType === "Appointment"
    )
    .map((entry) => entry.resource!)
    .filter(
      (resource): resource is fhir5.Appointment => resource !== undefined
    );
}

function extractSlots(bundle?: fhir5.Bundle): fhir5.Slot[] {
  if (!bundle?.entry) return [];

  return bundle.entry
    .filter(
      (entry): entry is fhir5.BundleEntry<fhir5.Slot> =>
        entry.resource?.resourceType === "Slot"
    )
    .map((entry) => entry.resource!)
    .filter((resource): resource is fhir5.Slot => resource !== undefined);
}

/**
 * Extrai apenas os recursos do tipo Schedule do bundle
 * @param bundle Bundle retornado pela API
 * @returns Array de Schedule
 */

/**
 * Hook para buscar estatísticas de pagamentos
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchStats } = usePaymentNoticeStats({
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31',
 *   selectedLocation: 'loc1',
 *   selectedPractitioner: 'pract1',
 * });
 *
 * // Buscar dados
 * useEffect(() => {
 *   fetchStats();
 * }, [fetchStats]);
 * ```
 */
export function usePaymentNoticeStats(
  props: UsePaymentNoticeStatsProps
): UsePaymentNoticeStatsResult {
  const { access } = useSessionContext();

  const [data, setData] = useState<{
    paymentNotices: fhir5.PaymentNotice[];
    patients: fhir5.Patient[];
    slots: fhir5.Slot[];
    appointments: fhir5.Appointment[];
  }>({
    paymentNotices: [],
    patients: [],
    slots: [],
    appointments: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Mantém uma referência estável dos parâmetros
  const paramsRef = useRef(props);

  // Atualiza a referência quando as props mudam
  useEffect(() => {
    paramsRef.current = props;
  }, [props]);

  const fetchStats = useCallback(async () => {
    const currentParams = paramsRef.current;

    try {
      setLoading(true);
      setError(null);

      if (!access?.organizationId) {
        throw new Error("ID da organização não encontrado");
      }

      const response = await GetPaymentNoticeReport({
        organizationId: access.organizationId,
        startDate: currentParams.startDate,
        endDate: currentParams.endDate,
        locationId:
          currentParams.selectedLocation === "all"
            ? undefined
            : currentParams.selectedLocation,
        practitionerId:
          currentParams.selectedPractitioner === "all" ||
          !currentParams.selectedPractitioner?.length
            ? undefined
            : `${oids.cpf}-${currentParams.selectedPractitioner
                ?.split("-")
                .pop()}`,
        paymentStatus:
          currentParams.paymentStatus === "active"
            ? "missing"
            : currentParams.paymentStatus,
        pageSize: currentParams.pageSize || 1000,
      });

      // Extrai PaymentNotices, Patients, Slots e Schedules do bundle
      const paymentNotices = extractPaymentNotices(response.data);
      const patients = extractPatients(response.data);
      const slots = extractSlots(response.data);
      const appointments = extractAppointments(response.data);

      setData({ paymentNotices, patients, slots, appointments });
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar estatísticas de pagamento")
      );
      console.error("usePaymentNoticeStats error:", err);
    } finally {
      setLoading(false);
    }
  }, []); // fetchStats não tem dependências externas

  return {
    data,
    loading,
    error,
    fetchStats,
  };
}
